import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../hooks/useProjects';
import { useAnalytics } from '../hooks/useAnalytics';
import { usePermissions } from '../hooks/usePermissions';
import Layout from '../components/layout/Layout';
import ChartSection from '../components/analytics/ChartSection';
import PermissionWrapper from '../components/ui/PermissionWrapper';
import apiClient from '../api/apiClient';

const Analytics = () => {
  const { projectId } = useParams();
  const { currentUser } = useAuth();
  const { useProject } = useProjects();
  const { data: project } = useProject(projectId);
  const { isSubUser } = usePermissions();
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [error, setError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [useOptimizedEndpoint, setUseOptimizedEndpoint] = useState(true);

  // Custom Analytics State
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [customAnalysisResult, setCustomAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [customAnalysisError, setCustomAnalysisError] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // Saved Analyses State
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [analysisStats, setAnalysisStats] = useState(null);
  const [isLoadingSavedAnalyses, setIsLoadingSavedAnalyses] = useState(false);
  const [savedAnalysesError, setSavedAnalysesError] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [isViewingAnalysis, setIsViewingAnalysis] = useState(false);
  const [analysesPage, setAnalysesPage] = useState(1);
  const [totalAnalyses, setTotalAnalyses] = useState(0);
  const [analysesFilter, setAnalysesFilter] = useState({
    starred: false,
    tags: '',
    search: ''
  });

  // Date range for saved analyses filtering
  const [savedAnalysesDateRange, setSavedAnalysesDateRange] = useState({
    start: '',
    end: ''
  });

  // Tab state
  const [activeTab, setActiveTab] = useState('monthly'); // 'custom', 'saved', 'monthly'

  const { useBasicStats, useReport, useGenerateReport } = useAnalytics();
  
  useEffect(() => {
    console.log('Analytics component mounted with projectId:', projectId);
    console.log('Selected year:', selectedYear);
    console.log('Selected month:', selectedMonth);
    console.log('Using optimized endpoint:', useOptimizedEndpoint);
  }, [projectId, selectedYear, selectedMonth, useOptimizedEndpoint]);

  const { 
    data: basicStats, 
    isLoading: isStatsLoading, 
    error: statsError 
  } = useBasicStats(projectId, selectedYear, selectedMonth, useOptimizedEndpoint);
  
  const { data: report, isLoading: isReportLoading } = useReport(projectId, selectedYear, selectedMonth);
  const generateReportMutation = useGenerateReport();

  useEffect(() => {
    console.log('Stats loading:', isStatsLoading);
    console.log('Report loading:', isReportLoading);
    console.log('Basic stats data structure:', basicStats);
    console.log('Basic stats totalSessions:', basicStats?.data?.totalSessions);
    console.log('Basic stats data path:', basicStats?.data);
    console.log('Report:', report);
  }, [isStatsLoading, isReportLoading, basicStats, report]);

  // Handle MongoDB memory errors
  useEffect(() => {
    if (statsError) {
      console.error('Stats error:', statsError);
      
      // Check if it's a MongoDB memory error
      if (statsError.message?.includes('Sort exceeded memory limit') || 
          statsError.response?.data?.message?.includes('Sort exceeded memory limit')) {
        
        console.log('MongoDB memory error detected, switching to optimized endpoint');
        setError('Large dataset detected. Switching to optimized data processing...');
        
        // If we're already using the optimized endpoint but still getting errors,
        // we need to further optimize or consider upgrading MongoDB
        if (useOptimizedEndpoint) {
          setError('Your dataset is too large for the current database plan. Consider upgrading to a paid MongoDB plan.');
        } else {
          setUseOptimizedEndpoint(true);
        }
      } else {
        setError(`Error loading analytics: ${statsError.message}`);
      }
    } else {
      setError(null);
    }
  }, [statsError, useOptimizedEndpoint]);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handleGenerateReport = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      console.log('Generating report for:', { projectId, year: selectedYear, month: selectedMonth });
      await generateReportMutation.mutateAsync({
        projectId,
        year: selectedYear,
        month: selectedMonth
      });
      console.log('Report generation completed');
    } catch (err) {
      setError('Failed to generate report. Please try again.');
      console.error('Error generating report:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  // Custom Analytics Functions
  const fetchAvailableModels = async () => {
    try {
      const response = await apiClient.get(`/analytics/projects/${projectId}/llm-models`);
      console.log('[API Response] GET /analytics/projects/:id/llm-models', response);
      
      if (response.data && response.data.models) {
        setAvailableModels(response.data.models);
        if (response.data.models.length > 0) {
          setSelectedModel(response.data.models[0].id);
        }
      } else {
        console.warn('Invalid response structure:', response.data);
        setAvailableModels([]);
        setCustomAnalysisError('No models available - please configure API keys in your project settings');
      }
    } catch (err) {
      console.error('Error fetching available models:', err);
      setCustomAnalysisError('Failed to load available models');
    }
  };

  const performCustomAnalysis = async () => {
    if (!customPrompt.trim() || !selectedModel) {
      setCustomAnalysisError('Please enter a prompt and select a model');
      return;
    }

    setIsAnalyzing(true);
    setCustomAnalysisError(null);
    setCustomAnalysisResult(null);

    try {
      const response = await apiClient.post(`/analytics/projects/${projectId}/custom-analytics`, {
        prompt: customPrompt,
        model: selectedModel,
        dateRange: dateRange
      });

      console.log('[Custom Analytics Response]', response);
      setCustomAnalysisResult(response.data);
    } catch (err) {
      console.error('Error performing custom analysis:', err);
      setCustomAnalysisError(err.response?.data?.message || 'Failed to perform analysis');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Saved Analyses Functions
  const fetchSavedAnalyses = async (page = 1, filters = analysesFilter) => {
    setIsLoadingSavedAnalyses(true);
    setSavedAnalysesError(null);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      
      if (filters.starred) params.append('starred', 'true');
      if (filters.tags) params.append('tags', filters.tags);
      if (filters.search) params.append('search', filters.search);
      
      // Add date range parameters
      if (savedAnalysesDateRange.start) params.append('startDate', savedAnalysesDateRange.start);
      if (savedAnalysesDateRange.end) params.append('endDate', savedAnalysesDateRange.end);

      console.log('[fetchSavedAnalyses] Making request with params:', params.toString());

      const response = await apiClient.get(`/analytics/projects/${projectId}/saved-analyses?${params}`);
      
      console.log('[fetchSavedAnalyses] Response:', response.data);
      console.log('[fetchSavedAnalyses] analyses:', response.data.analyses);
      console.log('[fetchSavedAnalyses] Response status:', response.status);
      console.log('[fetchSavedAnalyses] Has analyses?', !!response.data?.analyses);
      console.log('[fetchSavedAnalyses] Condition check:', response.status === 200 && response.data?.analyses);

      if (response.data?.analyses) {
        console.log('[fetchSavedAnalyses] Setting savedAnalyses with:', response.data.analyses);
        setSavedAnalyses(response.data.analyses);
        setTotalAnalyses(response.data.total);
        setAnalysesPage(page);
        console.log('[fetchSavedAnalyses] State should be updated now');
      } else {
        console.log('[fetchSavedAnalyses] Condition failed - not setting state');
      }
    } catch (err) {
      console.error('Error fetching saved analyses:', err);
      setSavedAnalysesError(err.response?.data?.message || 'Failed to load saved analyses');
    } finally {
      setIsLoadingSavedAnalyses(false);
    }
  };

  const fetchAnalysisStats = async () => {
    try {
      const response = await apiClient.get(`/analytics/projects/${projectId}/analysis-stats`);
      console.log("[fetchAnalysisStats] Response: ", response)
      if (response.data?.success) {
        setAnalysisStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching analysis stats:', err);
    }
  };

  const viewSavedAnalysis = async (analysisId) => {
    try {
      console.log('[viewSavedAnalysis] Making request for ID:', analysisId);
      const response = await apiClient.get(`/analytics/projects/${projectId}/saved-analyses/${analysisId}`);
      console.log('[viewSavedAnalysis] Response:', response.data);
      
      if (response.data?.analysis) {
        console.log('[viewSavedAnalysis] Setting selected analysis:', response.data.analysis);
        setSelectedAnalysis(response.data.analysis);
        setIsViewingAnalysis(true);
      } else {
        console.log('[viewSavedAnalysis] No analysis data found in response');
      }
    } catch (err) {
      console.error('Error fetching analysis:', err);
      setSavedAnalysesError('Failed to load analysis details');
    }
  };

  const updateSavedAnalysis = async (analysisId, updates) => {
    try {
      const response = await apiClient.patch(`/analytics/projects/${projectId}/saved-analyses/${analysisId}`, updates);
      console.log('[updateSavedAnalysis] Response:', response.data);
      
      if (response.data?.analysis) {
        // Refresh the list
        fetchSavedAnalyses(analysesPage, analysesFilter);

        if (selectedAnalysis?._id === analysisId) {
          console.log('Analysis is loaded for viewing: ', analysisId)
          setSelectedAnalysis(response.data.analysis);
        }
      }
    } catch (err) {
      console.error('Error updating analysis:', err);
      setSavedAnalysesError('Failed to update analysis');
    }
  };

  const deleteSavedAnalysis = async (analysisId) => {
    if (!window.confirm('Are you sure you want to delete this analysis?')) return;
    
    try {
      await apiClient.delete(`/analytics/projects/${projectId}/saved-analyses/${analysisId}`);
      // Refresh the list
      fetchSavedAnalyses(analysesPage, analysesFilter);
      fetchAnalysisStats();
      if (selectedAnalysis?._id === analysisId) {
        setSelectedAnalysis(null);
        setIsViewingAnalysis(false);
      }
    } catch (err) {
      console.error('Error deleting analysis:', err);
      setSavedAnalysesError('Failed to delete analysis');
    }
  };

  const handleStarToggle = async (analysisId, currentStarred) => {
    try {
      console.log(`[handleStarToggle] Toggling star for ${analysisId} from ${currentStarred} to ${!currentStarred}`);
      await updateSavedAnalysis(analysisId, { isStarred: !currentStarred });
      
      // Refresh analysis stats to update the starred count
      fetchAnalysisStats();
      
      console.log(`[handleStarToggle] Successfully toggled star for ${analysisId}`);
    } catch (error) {
      console.error('[handleStarToggle] Error toggling star:', error);
      setSavedAnalysesError('Failed to update favorite status');
    }
  };

  // Format analysis text for better display
  const formatAnalysisText = (text) => {
    if (!text) return '';
    
    return text
      // First handle markdown tables
      .replace(/\|(.+)\|/g, (match, content) => {
        const cells = content.split('|').map(cell => cell.trim());
        return '<tr>' + cells.map(cell => `<td style="padding: 8px; border: 1px solid #e5e7eb;">${cell}</td>`).join('') + '</tr>';
      })
      // Wrap table rows in table
      .replace(/(<tr>.*<\/tr>[\s\n]*)+/g, (match) => {
        const rows = match.split(/[\s\n]*<tr>/).filter(row => row.includes('</tr>'));
        const headerRow = rows[0] ? '<thead><tr>' + rows[0].replace(/<td/g, '<th').replace(/td>/g, 'th>') + '</tr></thead>' : '';
        const bodyRows = rows.slice(1).map(row => '<tr>' + row).join('');
        return `<table style="width: 100%; border-collapse: collapse; margin: 1em 0; font-size: 14px;">${headerRow}<tbody>${bodyRows}</tbody></table>`;
      })
      
      // Convert double line breaks to paragraph breaks
      .replace(/\n\n/g, '</p><p>')
      // Convert single line breaks to <br>
      .replace(/\n/g, '<br>')
      
      // Format headers (lines that start with ###)
      .replace(/^#{1,6}\s*(.+)$/gm, (match, content) => {
        const level = match.indexOf(' ');
        return `<h${Math.min(level, 6)} style="margin: 1.5em 0 0.5em 0; font-weight: 600; color: #1f2937; font-size: ${1.4 - (level * 0.1)}em; border-left: 4px solid #3b82f6; padding-left: 0.75em;">${content}</h${Math.min(level, 6)}>`;
      })
      
      // Format headers (lines that end with :)
      .replace(/^([^<\n|]+:)$/gm, '<h4 style="margin: 1.5em 0 0.5em 0; font-weight: 600; color: #1f2937; font-size: 1.1em; border-left: 4px solid #3b82f6; padding-left: 0.75em;">$1</h4>')
      
      // Format bullet points
      .replace(/^[\s]*[-•*]\s*(.+)$/gm, '<li>$1</li>')
      // Wrap consecutive list items in <ul>
      .replace(/(<li>.*<\/li>)(\s*<br>\s*<li>.*<\/li>)*/g, (match) => {
        return '<ul style="margin: 1em 0; padding-left: 1.5em;">' + match.replace(/<br>\s*/g, '') + '</ul>';
      })
      
      // Format numbered lists
      .replace(/^[\s]*(\d+\.)\s*(.+)$/gm, '<li><strong>$1</strong> $2</li>')
      // Wrap numbered lists in <ol>
      .replace(/(<li><strong>\d+\.<\/strong>.*<\/li>)(\s*<br>\s*<li><strong>\d+\.<\/strong>.*<\/li>)*/g, (match) => {
        return '<ol style="margin: 1em 0; padding-left: 1.5em;">' + match.replace(/<br>\s*/g, '').replace(/<strong>(\d+\.)<\/strong>/g, '$1') + '</ol>';
      })
      
      // Format bold text (**text**)
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Format italic text (*text*)
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      
      // Highlight numbers and percentages (but not inside HTML tags or CSS)
      .replace(/(?<![\w#])(\d+(?:\.\d+)?%)(?![^<]*>)/g, '<span style="background: #fef3c7; padding: 2px 4px; border-radius: 3px; font-weight: 500;">$1</span>')
      .replace(/(?<![\w#])(\b\d{1,3}(?:,\d{3})*(?:\.\d+)?\b)(?![^<]*>|[a-fA-F0-9])/g, '<span style="font-weight: 500; color: #059669;">$1</span>')
      
      // Wrap everything in paragraphs if not already wrapped
      .replace(/^(?!<[hou])/gm, '<p>')
      .replace(/(?<!>)$/gm, '</p>')
      
      // Clean up empty paragraphs
      .replace(/<p><\/p>/g, '')
      
      // Clean up paragraph breaks around headers, lists, and tables
      .replace(/<\/p>\s*(<h[1-6])/g, '$1')
      .replace(/(<\/h[1-6]>)\s*<p>/g, '$1')
      .replace(/<\/p>\s*(<[ou]l>)/g, '$1')
      .replace(/(<\/[ou]l>)\s*<p>/g, '$1')
      .replace(/<\/p>\s*(<table)/g, '$1')
      .replace(/(<\/table>)\s*<p>/g, '$1');
  };

  // Load available models on component mount
  useEffect(() => {
    if (projectId) {
      fetchAvailableModels();
    }
  }, [projectId]);

  // Load saved analyses when component mounts or projectId changes
  useEffect(() => {
    if (projectId) {
      fetchSavedAnalyses();
      fetchAnalysisStats();
    }
  }, [projectId]);

  // Refresh saved analyses when filters change
  useEffect(() => {
    if (projectId) {
      fetchSavedAnalyses(1, analysesFilter);
    }
  }, [analysesFilter, savedAnalysesDateRange]);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8" style={{width: 'calc(100% - 20px)'}}>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics Dashboard</h1>
          <p className="text-gray-600">Project: {project?.name}</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('monthly')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'monthly'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📊 Monthly Analysis Report
              </button>
              <button
                onClick={() => setActiveTab('custom')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'custom'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🤖 Custom AI Analytics
              </button>
              <button
                onClick={() => setActiveTab('saved')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'saved'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📚 Saved Analysis History
              </button>
            </nav>
          </div>
        </div>

        {/* Monthly Analysis Report Tab */}
        {activeTab === 'monthly' && (
          <>
            {/* Month/Year Selection */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 pl-2 pr-2 pb-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
            <h2 className="text-xl font-semibold mb-2 md:mb-0">Analytics Period</h2>
            <div className="flex gap-4">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="px-3 py-2 border rounded-md text-smd"
              >
                {months.map((month, index) => (
                  <option key={month} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-3 py-2 border rounded-md text-smd ml-2"
              >
                {[...Array(2035 - 2024 + 1)].map((_, i) => {
                  const year = 2035 - i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md mb-4">
              <p>{error}</p>
            </div>
          )}

          {/* Basic Statistics */}
          {basicStats && (
            <div className="pl-2 pr-2 pb-2">
              <h3 className="text-md font-medium text-gray-700 mb-4 border-b pb-2">Chat Activity Overview</h3>
              <div className={`grid gap-4 ${isSubUser ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'}`}>
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Total Sessions</h4>
                  <p className="text-2xl font-bold text-blue-600">{basicStats?.data?.totalSessions || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Total Messages</h4>
                  <p className="text-2xl font-bold text-green-600">{basicStats?.data?.totalMessages || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Engagement Rate</h4>
                  <p className="text-2xl font-bold text-purple-600">{basicStats?.data?.engagementRate || 0}%</p>
                </div>
                {!isSubUser && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-600 mb-1">Real Conversations</h4>
                    <p className="text-2xl font-bold text-indigo-600">{basicStats?.data?.meaningfulSessionsCount || 0}</p>
                  </div>
                )}
                {!isSubUser && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-600 mb-1">Messages in Convs</h4>
                    <p className="text-2xl font-bold text-teal-600">{basicStats?.data?.meaningfulMessagesCount || 0}</p>
                  </div>
                )}
                {!isSubUser && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-600 mb-1">Avg Msgs/Conv</h4>
                    <p className="text-2xl font-bold text-amber-600">
                      {basicStats?.data?.meaningfulSessionsCount > 0
                        ? (basicStats?.data?.meaningfulMessagesCount / basicStats?.data?.meaningfulSessionsCount).toFixed(1)
                        : 0}
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-3 text-xs text-gray-500 flex gap-4 flex-wrap">
                {!isSubUser && (
                  <span className="flex items-center"><span className="w-3 h-3 bg-indigo-600 inline-block mr-1 rounded-sm"></span> Real Conversations: Sessions with more than 2 messages</span>
                )}
                <span className="flex items-center"><span className="w-3 h-3 bg-purple-600 inline-block mr-1 rounded-sm"></span> Engagement Rate: Percentage of sessions with meaningful interaction</span>
                <span className="flex items-center"><span className="w-3 h-3 bg-amber-600 inline-block mr-1 rounded-sm"></span> Avg Msgs/Conv: Average messages in real conversations</span>
              </div>
            </div>
          )}
        </div>

        {/* Daily Statistics Chart */}
        {basicStats?.data?.dailyData && (
          <div className="mb-8">
            <ChartSection dailyData={basicStats.data.dailyData} isSubUser={isSubUser} />
          </div>
        )}

        {/* Report Generation Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 pl-2 pr-2 pb-2 py-2">
              <h2 className="text-xl font-semibold mb-4">Monthly Analysis Report</h2>
              <p className="text-gray-600 mb-4">
                Generate a new AI-powered analysis of your chatbot's performance for the selected period.
                This report will include insights about chat patterns, potential issues, and improvement recommendations.
              </p>
              <div className="space-y-4">
                {isGenerating ? (
                  <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md flex items-center">
                    <svg className="animate-spin h-5 w-5 mr-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <div>
                      <p className="font-medium">Please wait, report is being generated...</p>
                      <p className="text-sm mt-1">This process may take a few minutes as we analyze your chat data.</p>
                    </div>
                  </div>
                ) : (
                  <PermissionWrapper section="generateReports">
                    <button
                      onClick={handleGenerateReport}
                      disabled={isGenerating || isReportLoading}
                      className={`px-4 py-2 rounded-md text-white text-smd font-medium w-full sm:w-auto ${
                        (isGenerating || isReportLoading)
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {isReportLoading ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading Report...
                        </span>
                      ) : 'Generate New Report'}
                    </button>
                  </PermissionWrapper>
                )}
                {isReportLoading && !isGenerating && (
                  <div className="text-sm text-gray-600">
                    <p>Please wait while we load your existing report.</p>
                  </div>
                )}
              </div>
            </div>

        {/* Report Display Section */}
        {isReportLoading ? (
          <div className="bg-white rounded-lg shadow-md p-6 pl-2 pr-2 pb-2 py-2">
            <div className="flex items-center justify-center space-x-4">
              <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-lg text-gray-700">Loading your report...</span>
            </div>
          </div>
        ) : report?.data && (
          <div className="space-y-6">
            {/* Executive Summary */}
            <div className="bg-white rounded-lg shadow-md p-6 pl-2 pr-2 pb-2 py-2">
              <h2 className="text-xl font-semibold mb-4">Executive Summary</h2>
              {report.data.summary?.overview && (
                <p className="text-gray-700 mb-4">{report.data.summary.overview}</p>
              )}
              
              {/* Key Metrics */}
              {report.data.summary?.keyMetrics && Object.keys(report.data.summary.keyMetrics).length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {Object.entries(report.data.summary.keyMetrics).map(([key, value]) => (
                    <div key={key} className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500">{key}</h3>
                      <p className="text-lg font-semibold text-gray-900">{value}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Notable Patterns */}
              {report.data.summary?.notablePatterns?.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Notable Patterns</h3>
                  <ul className="space-y-2">
                    {report.data.summary.notablePatterns.map((pattern, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        <span className="text-gray-700">{pattern}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* User Engagement Analysis */}
            <div className="bg-white rounded-lg shadow-md p-6 pl-2 pr-2 pb-2 py-2">
              <h2 className="text-xl font-semibold mb-4">User Engagement Analysis</h2>
              
              {/* Session Patterns */}
              {report.data.engagement?.sessionPatterns && Object.keys(report.data.engagement.sessionPatterns).length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Session Patterns</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(report.data.engagement.sessionPatterns).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-500">{key}</h4>
                        <p className="text-lg font-semibold text-gray-900">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* User Distribution */}
              {report.data.engagement?.userDistribution && Object.keys(report.data.engagement.userDistribution).length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">User Distribution</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(report.data.engagement.userDistribution).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-500">{key}</h4>
                        <p className="text-lg font-semibold text-gray-900">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Chat Types Overview */}
            {report.data.chatTypes?.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 pl-2 pr-2 pb-2 py-2">
                <h2 className="text-xl font-semibold mb-4">Chat Types Overview</h2>
                <div className="space-y-4">
                  {report.data.chatTypes.map((type, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-gray-700">{type.name}</span>
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-200 rounded-full h-2.5 mr-2">
                          <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${type.percentage}%` }}></div>
                        </div>
                        <span className="text-gray-900 font-medium">{type.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Issues Section */}
            {report.data.issues?.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 pl-2 pr-2 pb-2 py-2">
                <h2 className="text-xl font-semibold mb-4">Identified Issues</h2>
                <ul className="space-y-3">
                  {report.data.issues.map((issue, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-red-500 mr-2">⚠️</span>
                      <span className="text-gray-700">{issue}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Improvement Recommendations */}
            <div className="bg-white rounded-lg shadow-md p-6 pl-2 pr-2 pb-2 py-2">
              <h2 className="text-xl font-semibold mb-4">Improvement Recommendations</h2>
              
              {/* Prompt Improvements */}
              {report.data.promptImprovements?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Prompt Improvements</h3>
                  <ul className="space-y-2">
                    {report.data.promptImprovements.map((improvement, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-500 mr-2">💡</span>
                        <span className="text-gray-700">{improvement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Knowledge Base Additions */}
              {report.data.knowledgeBaseAdditions?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Knowledge Base Additions</h3>
                  <ul className="space-y-2">
                    {report.data.knowledgeBaseAdditions.map((addition, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-green-500 mr-2">📚</span>
                        <span className="text-gray-700">{addition}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* API Endpoint Recommendations */}
              {report.data.apiRecommendations?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">API Endpoint Recommendations</h3>
                  <ul className="space-y-2">
                    {report.data.apiRecommendations.map((recommendation, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-purple-500 mr-2">🔌</span>
                        <span className="text-gray-700">{recommendation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* System Improvements */}
              {report.data.systemImprovements?.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">System Improvements</h3>
                  <ul className="space-y-2">
                    {report.data.systemImprovements.map((improvement, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-orange-500 mr-2">⚙️</span>
                        <span className="text-gray-700">{improvement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Action Items */}
            <div className="bg-white rounded-lg shadow-md p-6 pl-2 pr-2 pb-2 py-2">
              <h2 className="text-xl font-semibold mb-4">Action Items</h2>
              
              {/* Quick Wins */}
              {report.data.actionItems?.quickWins?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Quick Wins</h3>
                  <ul className="space-y-2">
                    {report.data.actionItems.quickWins.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-green-500 mr-2">⚡</span>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Short-term Actions */}
              {report.data.actionItems?.shortTerm?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Short-term Actions</h3>
                  <ul className="space-y-2">
                    {report.data.actionItems.shortTerm.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-500 mr-2">📅</span>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Long-term Actions */}
              {report.data.actionItems?.longTerm?.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Long-term Actions</h3>
                  <ul className="space-y-2">
                    {report.data.actionItems.longTerm.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-purple-500 mr-2">🎯</span>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
          </>
        )}

        {/* Custom AI Analytics Tab */}
        {activeTab === 'custom' && (
          <>
            {/* Custom LLM Analytics Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 pl-2 pr-2 pb-2 py-2">
          <h2 className="text-xl font-semibold mb-4">🤖 Custom AI Analytics</h2>
          <p className="text-gray-600 mb-6">
            Ask your own questions about your chat data and get AI-powered insights using your preferred LLM model.
          </p>

          <div className="space-y-6">
            {/* Model Selection and Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI Model
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={availableModels.length === 0}
                >
                  {availableModels.length === 0 ? (
                    <option value="">No models available</option>
                  ) : (
                    availableModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name} ({model.provider})
                      </option>
                    ))
                  )}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Prompt Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Analytics Question
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Ask any question about your chat data. For example:
• What are the most common user complaints?
• Analyze user engagement patterns and suggest improvements
• What topics do users ask about most frequently?
• How can I improve my chatbot's response quality?
• Identify patterns in successful vs unsuccessful conversations"
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
              />
              <p className="text-xs text-gray-500 mt-1">
                Be specific about what insights you're looking for. The AI will analyze your chat session data to provide detailed answers.
              </p>
            </div>

            {/* Action Button */}
            <div className="flex justify-between items-center">
              <button
                onClick={performCustomAnalysis}
                disabled={isAnalyzing || !customPrompt.trim() || !selectedModel || availableModels.length === 0}
                className={`px-6 py-2 rounded-md text-white font-medium ${
                  isAnalyzing || !customPrompt.trim() || !selectedModel || availableModels.length === 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                {isAnalyzing ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analyzing...
                  </span>
                ) : (
                  '🚀 Analyze Data'
                )}
              </button>
              
              {availableModels.length === 0 && (
                <p className="text-sm text-red-600">
                  No AI models available. Please configure API keys in your project settings.
                </p>
              )}
            </div>

            {/* Error Display */}
            {customAnalysisError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                <p>{customAnalysisError}</p>
              </div>
            )}

            {/* Results Display */}
            {customAnalysisResult && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 sm:mb-0">
                      🧠 AI Analysis Results
                    </h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {customAnalysisResult.model}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {customAnalysisResult.sessionCount} sessions
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {customAnalysisResult.metadata?.tokensUsed || 'N/A'} tokens
                      </span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(customAnalysisResult.analysis);
                          // You could add a toast notification here
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        title="Copy analysis to clipboard"
                      >
                        📋 Copy
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Analysis Content */}
                <div className="px-6 py-6">
                  <div className="prose prose-blue max-w-none">
                    <div 
                      className="text-gray-800 leading-relaxed space-y-4"
                      style={{
                        fontSize: '15px',
                        lineHeight: '1.7'
                      }}
                      dangerouslySetInnerHTML={{
                        __html: formatAnalysisText(customAnalysisResult.analysis)
                      }}
                    />
                  </div>
                  
                  {/* Custom CSS for better formatting */}
                  <style jsx>{`
                    .prose ul {
                      margin: 1em 0;
                      padding-left: 1.5em;
                    }
                    .prose ul li {
                      margin: 0.5em 0;
                      list-style-type: disc;
                    }
                    .prose ol {
                      margin: 1em 0;
                      padding-left: 1.5em;
                    }
                    .prose ol li {
                      margin: 0.5em 0;
                      list-style-type: decimal;
                    }
                    .prose h4 {
                      margin: 1.5em 0 0.5em 0;
                      font-weight: 600;
                      color: #1f2937;
                      font-size: 1.1em;
                      border-left: 4px solid #3b82f6;
                      padding-left: 0.75em;
                    }
                    .prose p {
                      margin: 1em 0;
                    }
                    .prose strong {
                      color: #1f2937;
                      font-weight: 600;
                    }
                    .prose em {
                      color: #4b5563;
                      font-style: italic;
                    }
                  `}</style>
                </div>
                
                {/* Footer */}
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:justify-between text-xs text-gray-500">
                    <span>
                      📅 Generated on {new Date(customAnalysisResult.timestamp).toLocaleString()}
                    </span>
                    <span className="mt-1 sm:mt-0">
                      📊 Time Range: {customAnalysisResult.metadata?.timeRange || 'All time'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Sample Prompts */}
            {!customAnalysisResult && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Sample Questions to Get Started:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <button
                    onClick={() => setCustomPrompt("What are the most common user questions and how well does my chatbot handle them?")}
                    className="text-left text-blue-700 hover:text-blue-900 hover:underline p-2 rounded bg-blue-100 hover:bg-blue-200"
                  >
                    • Most common user questions analysis
                  </button>
                  <button
                    onClick={() => setCustomPrompt("Analyze conversation patterns and identify where users drop off or get frustrated.")}
                    className="text-left text-blue-700 hover:text-blue-900 hover:underline p-2 rounded bg-blue-100 hover:bg-blue-200"
                  >
                    • User drop-off and frustration analysis
                  </button>
                  <button
                    onClick={() => setCustomPrompt("What are the peak usage hours and how does user behavior differ throughout the day?")}
                    className="text-left text-blue-700 hover:text-blue-900 hover:underline p-2 rounded bg-blue-100 hover:bg-blue-200"
                  >
                    • Peak hours and behavior patterns
                  </button>
                  <button
                    onClick={() => setCustomPrompt("Provide specific recommendations to improve user engagement and conversation success rates.")}
                    className="text-left text-blue-700 hover:text-blue-900 hover:underline p-2 rounded bg-blue-100 hover:bg-blue-200"
                  >
                    • Engagement improvement recommendations
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
          </>
        )}

        {/* Saved Analysis History Tab */}
        {activeTab === 'saved' && (
          <>
            {/* Saved Analyses Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 pl-2 pr-2 pb-2 py-2">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
            <h2 className="text-xl font-semibold mb-4 sm:mb-0">📚 Saved Analysis History</h2>
            <div className="flex flex-wrap gap-2">
              {analysisStats && (
                <>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {analysisStats.totalAnalyses} Total
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    {analysisStats.starredCount} Starred
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {analysisStats.totalTokensUsed?.toLocaleString() || 0} Tokens Used
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Analysis Viewing Modal */}
          {isViewingAnalysis && selectedAnalysis && (
            <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-start justify-center pt-4 pb-4">
              <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-full overflow-hidden">
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {selectedAnalysis.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {selectedAnalysis.model}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {selectedAnalysis.sessionCount} sessions
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {selectedAnalysis.metadata?.tokensUsed || 'N/A'} tokens
                        </span>
                        <span className="text-gray-500">
                          {new Date(selectedAnalysis.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleStarToggle(selectedAnalysis._id, selectedAnalysis.isStarred)}
                        className={`p-2 rounded-full transition-all duration-200 ${selectedAnalysis.isStarred 
                          ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200 shadow-sm' 
                          : 'bg-gray-100 text-gray-400 hover:bg-yellow-100 hover:text-yellow-500'
                        }`}
                        title={selectedAnalysis.isStarred ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <span className="text-lg">
                          {selectedAnalysis.isStarred ? '⭐' : '☆'}
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(selectedAnalysis.analysis);
                          // Could add toast notification here
                        }}
                        className="p-2 text-gray-500 hover:text-gray-700 rounded-full"
                        title="Copy analysis"
                      >
                        📋
                      </button>
                      <button
                        onClick={() => setIsViewingAnalysis(false)}
                        className="p-2 text-gray-500 hover:text-gray-700 rounded-full"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="overflow-y-auto max-h-96">
                  <div className="px-6 py-6">
                    {/* Original Prompt */}
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Original Question:</h4>
                      <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-800 border-l-4 border-blue-500">
                        {selectedAnalysis.prompt}
                      </div>
                    </div>

                    {/* Analysis Content */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Analysis Results:</h4>
                      <div className="prose prose-blue max-w-none">
                        <div 
                          className="text-gray-800 leading-relaxed space-y-4"
                          style={{ fontSize: '15px', lineHeight: '1.7' }}
                          dangerouslySetInnerHTML={{
                            __html: formatAnalysisText(selectedAnalysis.analysis)
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>📊 Date Range: {selectedAnalysis.metadata?.dateRange || 'All time'}</span>
                    <span>Created by: {selectedAnalysis.createdBy?.name || 'Unknown'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters and Search */}
          <div className="mb-6 space-y-4">
            <div className="text-sm text-gray-600 mb-2">
              Filter saved analyses by text search, tags, creation date, or starred status:
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Search analyses..."
                  value={analysesFilter.search}
                  onChange={(e) => setAnalysesFilter({...analysesFilter, search: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tags</label>
                <input
                  type="text"
                  placeholder="Filter by tags..."
                  value={analysesFilter.tags}
                  onChange={(e) => setAnalysesFilter({...analysesFilter, tags: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Created After</label>
                <input
                  type="date"
                  value={savedAnalysesDateRange.start}
                  onChange={(e) => setSavedAnalysesDateRange({...savedAnalysesDateRange, start: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Created Before</label>
                <input
                  type="date"
                  value={savedAnalysesDateRange.end}
                  onChange={(e) => setSavedAnalysesDateRange({...savedAnalysesDateRange, end: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Options</label>
                <label className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    checked={analysesFilter.starred}
                    onChange={(e) => setAnalysesFilter({...analysesFilter, starred: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm">Starred only</span>
                </label>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Actions</label>
                <button
                  onClick={() => {
                    setAnalysesFilter({ starred: false, tags: '', search: '' });
                    setSavedAnalysesDateRange({ start: '', end: '' });
                    setAnalysesPage(1);
                  }}
                  className="w-full px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 mt-1"
                >
                  Clear All
                </button>
              </div>
            </div>

            {/* Popular Tags */}
            {analysisStats?.popularTags?.length > 0 && (
              <div>
                <span className="text-sm text-gray-600 mr-2">Popular tags:</span>
                <div className="inline-flex flex-wrap gap-1">
                  {analysisStats.popularTags.slice(0, 5).map((tag) => (
                    <button
                      key={tag._id}
                      onClick={() => setAnalysesFilter({...analysesFilter, tags: tag._id})}
                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      {tag._id} ({tag.count})
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Error Display */}
          {savedAnalysesError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
              <p>{savedAnalysesError}</p>
            </div>
          )}

          {/* Analyses List */}
          {isLoadingSavedAnalyses ? (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="ml-2 text-gray-600">Loading saved analyses...</span>
            </div>
          ) : savedAnalyses.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-6xl mb-4">📊</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Saved Analyses Yet</h3>
              <p className="text-gray-600 mb-4">
                Your AI analyses will be automatically saved here for future reference. {savedAnalyses.length}
              </p>
              <p className="text-sm text-gray-500">
                Run a custom analysis above to get started!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {savedAnalyses.map((analysis) => (
                <div
                  key={analysis._id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium text-gray-900">{analysis.title}</h3>
                        {analysis.isStarred && <span className="text-yellow-500">⭐</span>}
                      </div>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{analysis.prompt}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800">
                          {analysis.model}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded bg-green-100 text-green-800">
                          {analysis.sessionCount} sessions
                        </span>
                        <span className="text-gray-500">
                          {new Date(analysis.createdAt).toLocaleDateString()}
                        </span>
                        {analysis.tags?.length > 0 && (
                          <span className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                            🏷️ {analysis.tags.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-4">
                      <button
                        onClick={() => viewSavedAnalysis(analysis._id)}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                        title="View analysis"
                      >
                        👁️
                      </button>
                      <button
                        onClick={() => handleStarToggle(analysis._id, analysis.isStarred)}
                        className={`p-2 rounded transition-all duration-200 ${analysis.isStarred 
                          ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200 shadow-sm' 
                          : 'bg-gray-100 text-gray-400 hover:bg-yellow-100 hover:text-yellow-500'
                        }`}
                        title={analysis.isStarred ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <span className="text-lg">
                          {analysis.isStarred ? '⭐' : '☆'}
                        </span>
                      </button>
                      <button
                        onClick={() => deleteSavedAnalysis(analysis._id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        title="Delete analysis"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalAnalyses > 10 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={() => fetchSavedAnalyses(analysesPage - 1, analysesFilter)}
                disabled={analysesPage <= 1}
                className={`px-3 py-2 rounded-md text-sm ${
                  analysesPage <= 1 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {analysesPage} of {Math.ceil(totalAnalyses / 10)}
              </span>
              <button
                onClick={() => fetchSavedAnalyses(analysesPage + 1, analysesFilter)}
                disabled={analysesPage >= Math.ceil(totalAnalyses / 10)}
                className={`px-3 py-2 rounded-md text-sm ${
                  analysesPage >= Math.ceil(totalAnalyses / 10)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>

            
          </>
        )}
      </div>
    </Layout>
  );
};

export default Analytics; 